import React, { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { ErrorNotification, ErrorMessage, createErrorMessage } from './ErrorNotification';
import { restClient } from './RestClient';

interface ResetPasswordProps {
  token: string;
  onSwitchToLogin: () => void;
}

export function ResetPassword({ token, onSwitchToLogin }: ResetPasswordProps) {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errors, setErrors] = useState<ErrorMessage[]>([]);

  const addError = (message: string, type: 'error' | 'warning' | 'info' = 'error') => {
    const error = createErrorMessage(message, type);
    setErrors(prev => [...prev, error]);

    setTimeout(() => {
      setErrors(prev => prev.filter(e => e.id !== error.id));
    }, 8000);
  };

  const dismissError = (id: string) => {
    setErrors(prev => prev.filter(e => e.id !== id));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword) {
      addError('Please enter a new password');
      return;
    }

    if (newPassword.length < 6) {
      addError('Password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      addError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const result = await restClient.resetPassword(token, newPassword);

      if (result.IsSuccess) {
        setIsSuccess(true);
      } else {
        switch (result.Error.code) {
          case 'INVALID_RESET_TOKEN':
            addError('This reset link is invalid. Please request a new one.');
            break;
          case 'EXPIRED_RESET_TOKEN':
            addError('This reset link has expired. Please request a new one.');
            break;
          case 'USED_RESET_TOKEN':
            addError('This reset link has already been used. Please request a new one.');
            break;
          default:
            addError(result.Error.description || 'Something went wrong. Please try again.');
        }
      }
    } catch (error) {
      console.error('Reset password error:', error);
      addError('Unable to connect to server. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <ErrorNotification errors={errors} onDismiss={dismissError} />

      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo auth-logo-login">
            <KeyRound size={28} />
          </div>
          <h1 className="auth-title">Set New Password</h1>
          <p className="auth-subtitle">
            {isSuccess ? 'Password reset successful' : 'Enter your new password below'}
          </p>
        </div>

        {isSuccess ? (
          <div className="mb-6">
            <div className="alert alert-success">
              Your password has been reset successfully. You can now sign in with your new password.
            </div>
            <button
              onClick={onSwitchToLogin}
              className="btn btn-lg btn-full btn-primary mt-4"
            >
              Sign in now
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mb-6">
            <div className="form-group-lg">
              <label className="form-label">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
                disabled={isLoading}
                className="form-control form-control-lg"
              />
            </div>

            <div className="form-group-lg">
              <label className="form-label">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                disabled={isLoading}
                className="form-control form-control-lg"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`btn btn-lg btn-full ${isLoading ? 'btn-secondary' : 'btn-primary'}`}
            >
              {isLoading ? (
                <>
                  <span className="spinner" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        )}

        {!isSuccess && (
          <div className="auth-footer">
            <p className="auth-footer-text">Remember your password?</p>
            <button
              onClick={onSwitchToLogin}
              disabled={isLoading}
              className="btn-link"
              style={{ opacity: isLoading ? 0.5 : 1 }}
            >
              Back to sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
